import { EventNames } from '@iheartjane/tracking';
import type { AppMode, PriceId, ReservationMode } from '@iheartjane/types';
import { Storage } from '@iheartjane/utils';
import type { Reducer } from '@reduxjs/toolkit';
import { createAction } from '@reduxjs/toolkit';
import isEmpty from 'lodash-es/isEmpty';
import pick from 'lodash-es/pick';
import type { NavigateFunction } from 'react-router';
import type { DeepReadonly } from 'ts-essentials';

import type {
  AddedOrUpdatedProductInCart,
  MoeEvent,
  UserPreferencesType,
} from '@jane/shared-ecomm/tracking';
import {
  extractTrackingTags,
  moeAddToBag,
  moeCheckout,
  track,
} from '@jane/shared-ecomm/tracking';
import { trackBranchCheckout } from '@jane/shared-ecomm/util';
// eslint-disable-next-line no-restricted-imports
import type {
  BundlePossibilities,
  Cart,
  CrmProviderType,
  CrmRedemption,
  MenuProduct,
  PendingCartProduct,
  Store,
  StoreSpecial,
} from '@jane/shared/models';
import type {
  Address,
  AlmostGone,
  CartLineItems,
  UserSegmentIds,
} from '@jane/shared/types';
import {
  formatTime,
  getActualUnitPrice,
  isStore,
  monolithPaths as paths,
  prepareCartProduct,
} from '@jane/shared/util';

import { openModal } from '../../customer/redux/application';
import {
  pickAnalyticsProductProperties,
  postAnalyticsEvent,
  postCheckoutAnalyticsEvent,
} from '../../lib/analyticsDispatching';
import { NotificationsService } from '../../services/notifications';
import type { CheckoutRequest, CheckoutResponse } from '../../sources/cart';
import { CartSource } from '../../sources/cart';
import { CartProductsSource } from '../../sources/cartProducts';
import {
  applyCrmRedemption as applyCrmRedemptionSource,
  removeCrmRedemption as removeCrmRedemptionSource,
} from '../../sources/crmIntegration';
import type { HeadlessCartProduct } from '../../types/headlessCart';
import { trackCartEvent, trackCheckoutSuccess } from '../cart';
import type { CustomerAction, CustomerThunkAction } from '../customerAction';
import type { AppDispatch, AppState, configureCustomerStore } from '../redux';
import {
  getBundlePossibilities,
  setTouchedProduct,
} from './bundlePossibilities';
import { getPosCart } from './posCart';

const OPEN_CART = 'cart/open';
export const openCart = createAction<void, typeof OPEN_CART>(OPEN_CART);

const SET_HEADLESS_V2_CART = 'cart/set_headless_v2';
export const setHeadlessV2Cart = createAction<
  void,
  typeof SET_HEADLESS_V2_CART
>(SET_HEADLESS_V2_CART);

const CLOSE_CART = 'cart/close';
export const closeCart = createAction<void, typeof CLOSE_CART>(CLOSE_CART);

const isEmptyCart = (cart: Cart) =>
  isEmpty(cart) || cart.uuid === '' || cart.products?.length === 0;

export const GET_CART = 'cart/get';
const initiateGetCart = createAction<void, typeof GET_CART>(GET_CART);
export const getCart =
  (forceRemote = false): CustomerThunkAction =>
  (dispatch, getState, extraArgsRef) => {
    const appMode = extraArgsRef?.current?.appMode;
    const {
      embeddedApp: { partnerId },
    } = getState();

    dispatch(initiateGetCart());
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getError = (err: any) => dispatch(getCartError(err));
    const getSuccess = ({ cart }: { cart: Cart }) =>
      dispatch(getLocalCartSuccess(cart, forceRemote));

    CartSource.getLocal(appMode!, partnerId).then(getSuccess, getError);
  };

const GET_REMOTE_CART = 'cart/get-remote';
const initiateGetRemoteCart = createAction<void, typeof GET_REMOTE_CART>(
  GET_REMOTE_CART
);
const GET_REMOTE_CART_SUCCESS = 'cart/get-remote-success';
const getRemoteCartSucceeded = createAction<
  void,
  typeof GET_REMOTE_CART_SUCCESS
>(GET_REMOTE_CART_SUCCESS);

export const getRemoteCart =
  (uuid: string, onSuccess?: () => void): CustomerThunkAction =>
  (dispatch, getState) => {
    dispatch(initiateGetRemoteCart());

    const { cart: cartState } = getState();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getError = (err: any) => dispatch(getCartError(err));
    const getRemoteCartSuccess = (result: { cart: Cart }) => {
      dispatch(getCartSuccess(result));
      dispatch(getRemoteCartSucceeded());
      onSuccess && onSuccess();
    };

    CartSource.getRemote(
      uuid,
      cartState.deliveryAddress,
      cartState.selectedPaymentMethod
    ).then(getRemoteCartSuccess, getError);
  };

const cartExpired = (cartState: CartState) => {
  const twoSecondsAgo = Date.now() - 2000;
  return (
    !cartState.wasLoadedAt ||
    twoSecondsAgo > new Date(cartState.wasLoadedAt).getTime()
  );
};

const GET_LOCAL_CART_SUCCESS = 'cart/get-local-success';
const initiateGetLocalCartSuccess = createAction<
  void,
  typeof GET_LOCAL_CART_SUCCESS
>(GET_LOCAL_CART_SUCCESS);
export const getLocalCartSuccess =
  (cart: Cart, forceRemote = false): CustomerThunkAction =>
  (dispatch, getState) => {
    dispatch(initiateGetLocalCartSuccess());
    const { cart: cartState } = getState();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getError = (err: any) => dispatch(getCartError(err));
    const getSuccess = (result: { cart: Cart }) => {
      dispatch(getCartSuccess(result));
      dispatch(getRemoteCartSucceeded());
    };

    if (isEmptyCart(cart)) {
      return dispatch(getCartSuccess({ cart }));
    }

    if (cartExpired(cartState) || forceRemote) {
      CartSource.getRemote(
        cart.uuid,
        cartState.deliveryAddress,
        cartState.selectedPaymentMethod
      ).then(getSuccess, getError);
    } else {
      dispatch(getCartSuccess({ cart: cartState.cart }));
    }
  };

export const GET_CART_SUCCESS = 'cart/get-success';
const getCartSucceeded = createAction<{ cart: Cart }, typeof GET_CART_SUCCESS>(
  GET_CART_SUCCESS
);
export const getCartSuccess =
  (result: { cart: Cart }): CustomerThunkAction =>
  (dispatch, getState, extraArgsRef) => {
    const { bundlePossibilities } = getState();

    const isHeadless = !!extraArgsRef?.current?.headlessStoreId;

    dispatchGetCartSuccessAction(
      getCartSucceeded,
      result,
      bundlePossibilities,
      dispatch,
      isHeadless
    );
  };

const REFRESH_CART = 'cart/refresh-cart';
const refreshCartSuccess = createAction<{ cart: Cart }, typeof REFRESH_CART>(
  REFRESH_CART
);
export const refreshCart =
  (cart: Cart): CustomerThunkAction =>
  (dispatch, getState) => {
    const { cart: cartState } = getState();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getError = (err: any) => dispatch(getCartError(err));
    const getSuccess = (result: { cart: Cart }) => {
      dispatch(refreshCartSuccess(result));
    };

    CartSource.getRemote(
      cart.uuid,
      cartState.deliveryAddress,
      cartState.selectedPaymentMethod
    ).then(getSuccess, getError);
  };

export const GET_USER_CART_SUCCESS = 'cart/get-user-cart-success';
const getUserCartSucceeded = createAction<
  { cart: Cart },
  typeof GET_USER_CART_SUCCESS
>(GET_USER_CART_SUCCESS);

const dispatchGetCartSuccessAction = (
  actionCreator: typeof getCartSucceeded | typeof getUserCartSucceeded,
  result: { cart: Cart },
  bundlePossibilities: {
    bundlePossibilities: BundlePossibilities;
    filterDiscountableProducts: boolean;
    showingBundlePossibilities: boolean;
    touchedProductId?: string | number;
  },
  dispatch: AppDispatch,
  isHeadless = false
) => {
  const { cart } = result;
  dispatch(actionCreator(result));
  if (isEmptyCart(cart) && !isHeadless) {
    return dispatch(clearCart());
  }

  if (
    bundlePossibilities.touchedProductId &&
    bundlePossibilities.showingBundlePossibilities
  ) {
    dispatch(getBundlePossibilities(cart.uuid));
  }
  return;
};

const getUserCartSuccess =
  (result: { cart: Cart }): CustomerThunkAction =>
  (dispatch, getState) => {
    const { bundlePossibilities } = getState();

    dispatchGetCartSuccessAction(
      getUserCartSucceeded,
      result,
      bundlePossibilities,
      dispatch
    );
  };

export const getCartForUser =
  (): CustomerThunkAction => (dispatch, getState, extraArgsRef) => {
    const {
      cart: {
        cart: { uuid },
      },
      embeddedApp: { partnerId },
    } = getState();

    dispatch(initiateGetCart());

    const getSuccess = ({ cart }: { cart: Cart }) =>
      dispatch(getUserCartSuccess({ cart }));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getError = (err: any) => dispatch(getCartError(err));

    const storeId = extraArgsRef?.current?.headlessStoreId ?? partnerId;
    const appMode = extraArgsRef?.current?.appMode;

    CartSource.getForUser(appMode, storeId, uuid).then(getSuccess, getError);
  };

// checks local storage & clears any existing cart. used in app modes like BrandEmbed where old carts aren't supported
export const resetCart =
  (): CustomerThunkAction => (dispatch, getState, extraArgsRef) => {
    const appMode = extraArgsRef?.current?.appMode;
    const {
      embeddedApp: { partnerId },
    } = getState();

    dispatch(initiateGetCart());

    const getSuccess = ({ cart }: { cart: Cart }) => {
      dispatch(getCartSuccess({ cart }));

      dispatch(clearCart());
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getError = (err: any) => dispatch(getCartError(err));

    CartSource.getLocal(appMode!, partnerId).then(getSuccess, getError);
  };

export const GET_CART_ERROR = 'cart/get-error';
export const getCartError = createAction<unknown, typeof GET_CART_ERROR>(
  GET_CART_ERROR
);

export const CART_CLEAR = 'cart/clear';
const initiateClearCart = createAction<void, typeof CART_CLEAR>(CART_CLEAR);
export const clearCart =
  (preserveRemoteCart?: boolean): CustomerThunkAction<Promise<unknown>> =>
  (dispatch, getState, extraArgsRef) => {
    const appMode = extraArgsRef?.current?.appMode;
    const {
      cart: { cart },
      embeddedApp: { partnerId },
    } = getState();

    dispatch(initiateClearCart());

    if (isEmptyCart(cart)) return Promise.resolve();

    const clearCartSuccessHandler = () => {
      Storage.remove('cart');
      Storage.remove(cartStorageKey(appMode, partnerId));
    };

    const clearCartErrorHandler = () => dispatch(clearCartError());

    const promise = preserveRemoteCart
      ? Promise.resolve()
      : CartSource.delete(cart.uuid);

    return promise.then(clearCartSuccessHandler, clearCartErrorHandler);
  };

export const CART_CLEAR_ERROR = 'cart/clear-error';
const clearCartError = createAction<void, typeof CART_CLEAR_ERROR>(
  CART_CLEAR_ERROR
);

export const CART_DELETE_ITEM = 'cart/delete-item';
const cartDeleteItemSuccess = createAction<
  CartDeleteItemPayload,
  typeof CART_DELETE_ITEM
>(CART_DELETE_ITEM);
export const deleteCartItem =
  (productId: string | number, priceId: PriceId): CustomerThunkAction =>
  (dispatch, getState) => {
    const {
      bundlePossibilities,
      cart: cartState,
      store: storeState,
    } = getState();
    const { cart } = cartState;

    const product: PendingCartProduct | undefined = cart.products.find(
      (product) => product.id === productId && product.price_id === priceId
    );

    const store = storeState?.store;
    const storeCurrency = isStore(store) ? store.currency_code : undefined;

    if (product) {
      const { name, category, brand, count } = product;
      const pricePerUnit =
        getActualUnitPrice(product as MenuProduct, product.price_id) || 0;
      track({
        event: EventNames.RemovedProductFromCart,
        productBrand: brand,
        productCategory: category,
        productId: productId.toString(),
        productName: name,
        productPrice: pricePerUnit,
        quantity: count,
        storeCurrency,
      });
    }

    if (bundlePossibilities.showingBundlePossibilities) {
      dispatch(setTouchedProduct(undefined));
    }

    const basePrice = product ? Number(product[`price_${priceId}`]) : 0;
    const discountedPrice = product
      ? Number(product[`discounted_price_${priceId}`])
      : 0;

    CartProductsSource.delete(productId, priceId, cart.uuid)
      .then(() => {
        dispatch(cartDeleteItemSuccess({ priceId, productId }));

        // only dispatch events after we are sure the request was successful
        postAnalyticsEvent({
          name: 'cartItemRemoval',
          properties: {
            priceId,
            productId,
            ...(basePrice > 0 && { price: basePrice }),
            ...(discountedPrice > 0 && { discountedPrice }),
          },
        });

        if (cart.products.length === 1) {
          postAnalyticsEvent({
            name: 'cartEmptied',
          });
        }

        // do not re-fetch cart if last product was just removed; cart will already have been deleted
        if (cart.products.length > 1) {
          dispatch(
            analyzeSpecials({
              promoCode: cart.promo_code,
              reservationMode: cart.reservation_mode,
              userGroupSpecialId: cartState.userGroupSpecialId,
            })
          );
          // we also need to refresh the pos cart calculation
          dispatch(getPosCart());
        }
      })
      .catch((err) => {
        const action = matchError(err);
        dispatch(action);
      });
  };

export const CART_UPDATE_PROMO_CODE = 'cart/update-promo-code';
const cartUpdatePromoCode = createAction<
  string | undefined | null,
  typeof CART_UPDATE_PROMO_CODE
>(CART_UPDATE_PROMO_CODE);
export const updateQueryPromoCode =
  (queryPromoCode: string | null): CustomerThunkAction =>
  (dispatch, getState, extraArgsRef) => {
    const appMode = extraArgsRef?.current?.appMode;
    const {
      embeddedApp: { partnerId },
    } = getState();
    const storageKey = `${cartStorageKey(appMode, partnerId)}-promo-code`;

    const storedPromoCode = Storage.get<string>(storageKey);
    const promoCode = queryPromoCode || storedPromoCode || undefined;

    dispatch(cartUpdatePromoCode(promoCode));

    // Clear promo code once it's applied to cart
    if (queryPromoCode === null) {
      Storage.remove(storageKey);
    }
  };

export const removeQueryPromoCode =
  (): CustomerThunkAction => (dispatch, getState, extraArgsRef) => {
    const appMode = extraArgsRef?.current?.appMode;
    const {
      embeddedApp: { partnerId },
    } = getState();
    const storageKey = `${cartStorageKey(appMode, partnerId)}-promo-code`;
    Storage.remove(storageKey);

    dispatch(cartUpdatePromoCode(null));
  };

export const CART_UPDATE_TAGS = 'cart/update-tags';
const cartUpdateTags = createAction<
  object | undefined,
  typeof CART_UPDATE_TAGS
>(CART_UPDATE_TAGS);
export const updateTags =
  (queryParams: object): CustomerThunkAction =>
  (dispatch, getState, extraArgsRef) => {
    const appMode = extraArgsRef?.current?.appMode;
    const {
      embeddedApp: { partnerId },
    } = getState();

    const storedTags = Storage.get<object>(
      `${cartStorageKey(appMode, partnerId)}-tags`
    );
    const urlTags = extractTrackingTags(queryParams);
    const payload = !isEmpty(urlTags)
      ? urlTags
      : !isEmpty(storedTags)
        ? storedTags
        : undefined;

    dispatch(cartUpdateTags(payload));
  };

const CART_UPDATE_TIP = 'cart/update-tip';
export const updateTip = createAction<string, typeof CART_UPDATE_TIP>(
  CART_UPDATE_TIP
);

const CART_UPDATE_PAYMENT_METHOD = 'cart/update-payment-method';
export const updatePaymentMethod = createAction<
  string | null,
  typeof CART_UPDATE_PAYMENT_METHOD
>(CART_UPDATE_PAYMENT_METHOD);

export const REMOVE_SPECIAL_HAS_CHANGED_FLAG =
  'cart/remove-special-has-changed-flag';
const initiateRemoveSpecialHasChangedFlag = createAction<
  void,
  typeof REMOVE_SPECIAL_HAS_CHANGED_FLAG
>(REMOVE_SPECIAL_HAS_CHANGED_FLAG);
export const removeSpecialHasChangedFlag =
  (): CustomerThunkAction => (dispatch, getState) => {
    const { cart: cartState } = getState();
    dispatch(initiateRemoveSpecialHasChangedFlag());
    CartSource.removeSpecialHasChangedFlag(cartState.cart.uuid).then(() =>
      dispatch(removeSpecialHasChangedFlagSuccess())
    );
  };

export const REMOVE_SPECIAL_HAS_CHANGED_FLAG_SUCCESS =
  'cart/remove-special-has-changed-flag-success';
export const removeSpecialHasChangedFlagSuccess = createAction<
  void,
  typeof REMOVE_SPECIAL_HAS_CHANGED_FLAG_SUCCESS
>(REMOVE_SPECIAL_HAS_CHANGED_FLAG_SUCCESS);

const REMOVE_BRAND_SPECIAL_HAS_CHANGED_FLAG_SUCCESS =
  'cart/remove-brand-speical-has-changed-flag-success';
export const removeBrandSpecialHasChangedFlag = createAction<
  void,
  typeof REMOVE_BRAND_SPECIAL_HAS_CHANGED_FLAG_SUCCESS
>(REMOVE_BRAND_SPECIAL_HAS_CHANGED_FLAG_SUCCESS);

export const DELETE_UNAVAILABLE_PRODUCTS = 'cart/delete-unavailable-products';
export const initiateDeleteUnavailableProducts = createAction<
  void,
  typeof DELETE_UNAVAILABLE_PRODUCTS
>(DELETE_UNAVAILABLE_PRODUCTS);
export const deleteUnavailableProducts =
  (productIds: (string | number)[]): CustomerThunkAction =>
  (dispatch, getState) => {
    const {
      cart: { cart },
    } = getState();

    if (!cart.uuid) {
      return;
    }

    dispatch(initiateDeleteUnavailableProducts());

    CartProductsSource.deleteUnavailableProducts(cart.uuid).then(() => {
      dispatch(deleteUnavailableProductsSuccess(productIds));
    });
  };

export const DELETE_UNAVAILABLE_PRODUCTS_SUCCESS =
  'cart/delete-unavailable-products-success';
export const deleteUnavailableProductsSuccess = createAction<
  (string | number)[],
  typeof DELETE_UNAVAILABLE_PRODUCTS_SUCCESS
>(DELETE_UNAVAILABLE_PRODUCTS_SUCCESS);

export const CLEAR_REMOVED_CRM_REDEMPTIONS =
  'cart/clear-removed-crm-redemptions';
export const clearRemovedCrmRedemptions = createAction<
  void,
  typeof CLEAR_REMOVED_CRM_REDEMPTIONS
>(CLEAR_REMOVED_CRM_REDEMPTIONS);

export const replaceCart =
  (
    replacementCartProduct: StoreAndCartProduct,
    moeTrack: (event: MoeEvent) => void
  ): CustomerThunkAction =>
  (dispatch) => {
    dispatch(clearCart()).then(() => {
      dispatch(addToCart(replacementCartProduct, false, moeTrack));
    });
  };

const DISMISS_CHECKOUT_ERROR = 'cart/dismiss-checkout-error';
export const dismissCheckoutError = createAction<
  void,
  typeof DISMISS_CHECKOUT_ERROR
>(DISMISS_CHECKOUT_ERROR);

export const removeCartUser =
  (): CustomerThunkAction => (dispatch, getState) => {
    const {
      cart: { cart, deliveryAddress },
    } = getState();
    if (cart.uuid === '') {
      return;
    }

    CartSource.update({
      delivery_address: deliveryAddress,
      user_id: null,
      uuid: cart.uuid,
    }).then((result) => {
      dispatch(getCartSuccess(result));
    });
  };

const CART_UPDATE_RESERVATION_MODE = 'cart/update-reservation-mode';
const cartUpdateReservationMode = createAction<
  { reservationMode: ReservationMode; userGroupSpecialId?: string | number },
  typeof CART_UPDATE_RESERVATION_MODE
>(CART_UPDATE_RESERVATION_MODE);
export const updateReservationMode =
  ({
    reservationMode,
    userGroupSpecialId,
  }: {
    reservationMode: ReservationMode;
    userGroupSpecialId?: string | number;
  }): CustomerThunkAction =>
  (dispatch, getState) => {
    const {
      cart: { cart, deliveryAddress },
    } = getState();
    if (!cart.uuid) return;

    dispatch(
      cartUpdateReservationMode({ reservationMode, userGroupSpecialId })
    );

    CartSource.update({
      delivery_address: deliveryAddress,
      reservation_mode: reservationMode,
      user_group_special_id: userGroupSpecialId,
      uuid: cart.uuid,
    }).then((result) => {
      dispatch({ payload: result, type: GET_CART_SUCCESS });

      // we also need to refresh the pos cart calculation
      dispatch(getPosCart());
    });
  };

export const CART_UPDATE_DELIVERY_ADDRESS = 'cart/update-delivery-address';
const initiateUpdateDeliveryAddress = createAction<
  CartUpdateDeliveryAddressPayload,
  typeof CART_UPDATE_DELIVERY_ADDRESS
>(CART_UPDATE_DELIVERY_ADDRESS);
export const updateDeliveryAddress =
  ({ deliveryAddress }: { deliveryAddress: Address }): CustomerThunkAction =>
  (dispatch, getState) => {
    const {
      cart: { cart, selectedPaymentMethod },
    } = getState();

    if ((deliveryAddress.street?.length || 0) > 0) {
      dispatch(initiateUpdateDeliveryAddress({ deliveryAddress }));

      if (cart.uuid) {
        CartSource.getRemote(
          cart.uuid,
          deliveryAddress,
          selectedPaymentMethod
        ).then((result) => {
          dispatch({ payload: result, type: GET_CART_SUCCESS });

          // we also need to refresh the pos cart calculation
          dispatch(getPosCart());
        });
      }
    }
  };

export const updateHeadlessDeliveryAddress =
  ({ deliveryAddress }: { deliveryAddress: Address }): CustomerThunkAction =>
  (dispatch) => {
    if ((deliveryAddress.street?.length || 0) > 0) {
      dispatch({
        payload: { deliveryAddress },
        type: CART_UPDATE_DELIVERY_ADDRESS,
      });
    }
  };

export const CART_REMOVE_DELIVERY_ADDRESS = 'cart/remove-delivery-address';
export const removeDeliveryAddress = createAction<
  void,
  typeof CART_REMOVE_DELIVERY_ADDRESS
>(CART_REMOVE_DELIVERY_ADDRESS);

export const ANALYZE_SPECIALS = 'cart/analyze-specials';
const initiateAnalyzeSpecials = createAction<void, typeof ANALYZE_SPECIALS>(
  ANALYZE_SPECIALS
);
export const analyzeSpecials =
  ({
    userGroupSpecialId,
    reservationMode,
    promoCode,
  }: {
    promoCode: string | undefined;
    reservationMode: ReservationMode;
    userGroupSpecialId: string | number | undefined;
  }): CustomerThunkAction =>
  (dispatch, getState) => {
    const {
      bundlePossibilities,
      cart: { cart, deliveryAddress },
    } = getState();

    if (isEmptyCart(cart)) {
      return;
    }

    dispatch(initiateAnalyzeSpecials());

    return CartSource.optimizeSpecials({
      delivery_address: deliveryAddress,
      promo_code: promoCode,
      reservation_mode: reservationMode,
      user_group_special_id: userGroupSpecialId,
      uuid: cart.uuid,
    }).then(
      ({ cart }) => {
        dispatch(analyzeSpecialsSuccess({ cart, userGroupSpecialId }));

        if (
          bundlePossibilities.touchedProductId &&
          bundlePossibilities.showingBundlePossibilities
        ) {
          dispatch(getBundlePossibilities(cart.uuid));
        }
      },
      (err) => dispatch(getCartError(err))
    );
  };

export const ANALYZE_SPECIALS_SUCCESS = 'cart/analyze-specials-success';
export const analyzeSpecialsSuccess = createAction<
  { cart: Cart; userGroupSpecialId?: string | number },
  typeof ANALYZE_SPECIALS_SUCCESS
>(ANALYZE_SPECIALS_SUCCESS);

export interface CrmParams extends CrmRedemption {
  crm_member_points: number;
}

const APPLY_CRM_REDEMPTION = 'cart/apply-crm-redemption';
const initiateApplyCrmRedemption = createAction<
  void,
  typeof APPLY_CRM_REDEMPTION
>(APPLY_CRM_REDEMPTION);
export const applyCrmRedemption =
  (crmParams: CrmParams): CustomerThunkAction =>
  (dispatch, getState) => {
    const {
      cart: { cart, deliveryAddress },
    } = getState();

    dispatch(initiateApplyCrmRedemption());

    applyCrmRedemptionSource(
      {
        ...crmParams,
        cart_uuid: cart.uuid,
      },
      deliveryAddress
    ).then(
      (result) => {
        if (result.error_message) {
          dispatch(applyCrmRedemptionError());
          return;
        }

        dispatch(updateCrmRedemptions({ cart: result.cart }));
      },
      () => dispatch(applyCrmRedemptionError())
    );
  };

export const UPDATE_CRM_REDEMPTIONS = 'cart/update-crm-redemptions';
const updateCrmRedemptions = createAction<
  { cart: Cart },
  typeof UPDATE_CRM_REDEMPTIONS
>(UPDATE_CRM_REDEMPTIONS);

const APPLY_CRM_REDEMPTION_ERROR = 'cart/apply-crm-redemption-error';
const applyCrmRedemptionError = createAction<
  void,
  typeof APPLY_CRM_REDEMPTION_ERROR
>(APPLY_CRM_REDEMPTION_ERROR);

export const removeCrmRedemption =
  (id: string | number, crmProvider: CrmProviderType): CustomerThunkAction =>
  (dispatch, getState) => {
    const {
      cart: { cart, userGroupSpecialId },
    } = getState();

    removeCrmRedemptionSource(id, crmProvider).then(
      () => {
        dispatch(removeCrmRedemptionSuccess({ id }));
        dispatch(
          analyzeSpecials({
            promoCode: cart.promo_code,
            reservationMode: cart.reservation_mode,
            userGroupSpecialId,
          })
        );
      },
      () => dispatch(removeCrmRedemptionError())
    );
  };

export const REMOVE_CRM_REDEMPTION_SUCCESS =
  'cart/remove-crm-redemption-success';
const removeCrmRedemptionSuccess = createAction<
  { id: string | number },
  typeof REMOVE_CRM_REDEMPTION_SUCCESS
>(REMOVE_CRM_REDEMPTION_SUCCESS);

const REMOVE_CRM_REDEMPTION_ERROR = 'cart/remove-crm-redemption-error';
const removeCrmRedemptionError = createAction<
  void,
  typeof REMOVE_CRM_REDEMPTION_ERROR
>(REMOVE_CRM_REDEMPTION_ERROR);

const PAYTENDER_CHECKOUT_CANCELLED = 'cart/paytender-checkout-cancelled';
export const paytenderCheckoutCancelled = createAction<
  void,
  typeof PAYTENDER_CHECKOUT_CANCELLED
>(PAYTENDER_CHECKOUT_CANCELLED);

const SET_USER_GROUP_SPECIAL_ID = 'cart/set-user-group-special-id';
export const setUserGroupSpecialId = createAction<
  { id: number | undefined },
  typeof SET_USER_GROUP_SPECIAL_ID
>(SET_USER_GROUP_SPECIAL_ID);

const FIX_UNAVAILABLE_CHECKOUT = 'cart/fix-unavailable-checkout';
export const fixUnavailableCheckout = createAction<
  void,
  typeof FIX_UNAVAILABLE_CHECKOUT
>(FIX_UNAVAILABLE_CHECKOUT);

const UNAVAILABLE_CHECKOUT_FIXED = 'cart/unavailable-checkout-fixed';
export const unavailableCheckoutFixed = createAction<
  void,
  typeof UNAVAILABLE_CHECKOUT_FIXED
>(UNAVAILABLE_CHECKOUT_FIXED);

const handleChangedCartError = (err: string, dispatch: AppDispatch) => {
  if (err.indexOf('Your total has been updated') > -1) {
    dispatch(getCart());
  }
  if (
    err.indexOf('One or more products in your cart are no longer available') >
    -1
  ) {
    dispatch(getCart());
    dispatch(fixUnavailableCheckout());
  }
};

export const CHECKOUT = 'cart/checkout';
const initiateCheckout = createAction<void, typeof CHECKOUT>(CHECKOUT);
export const checkout =
  ({
    checkout,
    isEmbeddedMode,
    isGuest,
    cartLineItems,
    moeTrack,
    navigate,
    onSuccess,
    isStackAdaptPixelEnabled,
  }: {
    cartLineItems: CartLineItems;
    checkout: CheckoutRequest;
    isEmbeddedMode: boolean;
    isGuest: boolean;
    isStackAdaptPixelEnabled: boolean;
    moeTrack: (event: MoeEvent) => void;
    navigate: NavigateFunction;
    onSuccess: () => void;
  }): CustomerThunkAction =>
  (dispatch) => {
    dispatch(initiateCheckout());

    CartSource.save(checkout)
      .then(
        (result: CheckoutResponse) => {
          onSuccess();

          dispatch(
            checkoutSuccess({
              cartLineItems,
              isEmbeddedMode,
              isGuest,
              moeTrack,
              navigate,
              result,
              paymentMethod: checkout.payment_method,
              isStackAdaptPixelEnabled,
            })
          );
        },
        (err) => {
          dispatch(checkoutError(err));
          handleChangedCartError(err, dispatch);
        }
      )
      .catch((err) => {
        dispatch(checkoutError(err));
      });
  };

export const CHECKOUT_SUCCESS = 'cart/checkout-success';
const checkoutSucceeded = createAction<
  CheckoutResponse['reservation'],
  typeof CHECKOUT_SUCCESS
>(CHECKOUT_SUCCESS);
const checkoutSuccess =
  ({
    result: { message, reservation, contact, warning_message },
    isEmbeddedMode,
    cartLineItems,
    isGuest,
    moeTrack,
    navigate,
    paymentMethod,
    isStackAdaptPixelEnabled,
  }: {
    cartLineItems: CartLineItems;
    isEmbeddedMode: boolean;
    isGuest: boolean;
    isStackAdaptPixelEnabled: boolean;
    moeTrack: (event: MoeEvent) => void;
    navigate: NavigateFunction;
    paymentMethod?: string;
    result: CheckoutResponse;
  }): CustomerThunkAction =>
  (dispatch, getState) => {
    const {
      cart: { cart, deliveryAddress },
      customer,
      store,
    } = getState();
    const { uuid } = cart;
    trackBranchCheckout(uuid);

    const { firstName, lastName, phone, email, birth_date } = customer;
    const total = Number(reservation.amount);

    const tz = isStore(store.store)
      ? store.store.time_zone_identifier
      : Intl.DateTimeFormat().resolvedOptions().timeZone;
    const delivery_finished_at = reservation.delivery_finished_at
      ? formatTime({
          time: reservation.delivery_finished_at,
          outputFormat: '',
          inputFormat: 'X',
          timezone: tz,
        })
      : undefined;
    const delivery_started_at = reservation.delivery_started_at
      ? formatTime({
          time: reservation.delivery_started_at,
          outputFormat: '',
          inputFormat: 'X',
          timezone: tz,
        })
      : undefined;

    postCheckoutAnalyticsEvent({
      cart: {
        ...cart,
        tags: getState().cart.tags,
        delivery_finished_at,
        delivery_started_at,
        message: reservation.message,
        payment_method: reservation.payment_method,
      },
      cartLineItems: {
        ...cartLineItems,
        total,
      },
      isStackAdaptPixelEnabled,
      deliveryAddress,
      orderCustomer: {
        birthDate: birth_date,
        email,
        firstName,
        lastName,
        phone,
      },
    });

    moeTrack(
      moeCheckout({
        cart,
        janePayStore:
          'id' in store.store
            ? !!store.store.jane_pay_integration?.enabled
            : false,
        storeId: 'id' in store.store ? store.store.id : null,
        storeName: 'name' in store.store ? store.store.name : null,
        paymentType: reservation.payment_method,
        reservationTime: new Date(delivery_started_at),
      })
    );

    trackCheckoutSuccess({
      cart,
      paymentMethod,
      store: isStore(store?.store) ? store.store : undefined,
    });

    if (!warning_message) {
      // TODO: Does this notification even show up?
      NotificationsService.success(message);
    }

    if (isGuest) {
      const { is_registered } = contact || {};
      navigate(
        paths.guestReservationReceipt(reservation.uuid, {
          is_registered,
        })
      );
    } else {
      const urlQuery = {};
      if (warning_message) {
        urlQuery['warning_message'] = warning_message;
      }
      const path = isEmbeddedMode
        ? paths.embeddedReservation(reservation.id, urlQuery)
        : paths.reservation(reservation.id, urlQuery);
      navigate(path);
    }

    dispatch(checkoutSucceeded(reservation));
  };

export const CHECKOUT_ERROR = 'cart/CHECKOUT_ERROR';
export const checkoutError = createAction<unknown, typeof CHECKOUT_ERROR>(
  CHECKOUT_ERROR
);

export const CART_UPDATE = 'cart/update';
const cartUpdateSuccess = createAction<
  PendingCartProduct | CartUpdatePayload,
  typeof CART_UPDATE
>(CART_UPDATE);
export const updateCart =
  (
    {
      product_id,
      count,
      price_id,
    }:
      | PendingCartProduct
      | {
          count: number;
          price_id: PriceId;
          product_id: number;
        },
    moeTrack: (event: MoeEvent) => void
  ): CustomerThunkAction =>
  (dispatch, getState) => {
    const { cart: cartState, bundlePossibilities, store } = getState();

    const { cart } = cartState;

    const cartProduct = cart.products.find(
      (product) =>
        product.id === product_id || product.product_id === product_id
    );
    const changeInCount = (count ?? 0) - (cartProduct?.count ?? 0);

    CartProductsSource.update(product_id, count, price_id, cart.uuid)
      .then((result) => {
        if (changeInCount > 0) {
          moeTrack(
            moeAddToBag({
              cartUuid: cart.uuid,
              product: { ...cartProduct, count: changeInCount },
              storeId: 'id' in store.store ? store.store.id : null,
              storeName: 'name' in store.store ? store.store.name : null,
            })
          );
        }

        dispatch(cartUpdateSuccess(result.cart_product));

        if (bundlePossibilities.showingBundlePossibilities) {
          dispatch(setTouchedProduct(product_id));
        }
        // NOTE: Cart object gets refetched in this call to analyze specials,
        // but does not replace the entire cart object - see ANALYZE_SPECIALS_SUCCESS
        // case in the reducer below.
        dispatch(
          analyzeSpecials({
            promoCode: cart.promo_code,
            reservationMode: cart.reservation_mode,
            userGroupSpecialId: cartState.userGroupSpecialId,
          })
        );

        // we also need to refresh the pos cart calculation
        dispatch(getPosCart());
      })
      .catch((err) => {
        const action = matchError(err);
        dispatch(action);
      });
  };

export const CART_ADD = 'cart/add';
const cartAddSuccess = createAction<CartAddPayload, typeof CART_ADD>(CART_ADD);
export const addToCart =
  (
    storeProduct: {
      product: PendingCartProduct;
      store: Store;
    },
    withoutDrawer: boolean,
    moeTrack: (event: MoeEvent) => void
  ): CustomerThunkAction<Promise<unknown>> =>
  (dispatch, getState, extraArgsRef) => {
    const appMode = extraArgsRef?.current?.appMode;
    const { cart, bundlePossibilities, application } = getState();
    return CartProductsSource.add({
      appMode,
      appModeName: appMode,
      cart_uuid: cart.cart.uuid,
      cartProduct: storeProduct.product,
      janeDeviceId: application?.janeDeviceId,
      store: storeProduct.store,
      tags: cart.tags,
    })
      .then((result) => {
        moeTrack(
          moeAddToBag({
            cartUuid: result.cart_uuid,
            product: storeProduct.product,
            storeId: storeProduct.store.id,
            storeName: storeProduct.store.name,
          })
        );

        dispatch(
          cartAddSuccess({
            ...storeProduct,
            cartUuid: result.cart_uuid,
            userAgent: navigator.userAgent,
          })
        );

        // we also need to refresh the pos cart calculation
        dispatch(getPosCart());

        if (bundlePossibilities.showingBundlePossibilities) {
          dispatch(setTouchedProduct(storeProduct.product.id));
        }

        dispatch(getCart(true));

        if (
          appMode !== 'headless' &&
          appMode !== 'bloom' && // This may be superfluous as items are added to cart directly from Bloom - Feb 6, 2025
          !withoutDrawer
        ) {
          dispatch(openCart());
        }
      })
      .catch((err) => {
        const action = matchError(err);
        dispatch(action);
      });
  };

const PREPARE_TO_REPLACE_CART = 'cart/prepare-to-replace-cart';
export const prepareToReplaceCart = createAction<
  CartState['replacementCart'],
  typeof PREPARE_TO_REPLACE_CART
>(PREPARE_TO_REPLACE_CART);

const CREATE_HEADLESS_CART_SUCCESS = 'cart/create-headless-success';
const createHeadlessCartSuccess = createAction<
  { cart: Cart },
  typeof CREATE_HEADLESS_CART_SUCCESS
>(CREATE_HEADLESS_CART_SUCCESS);

const CREATE_HEADLESS_CART_ERROR = 'cart/create-headless-error';
const createHeadlessCartError = createAction<
  void,
  typeof CREATE_HEADLESS_CART_ERROR
>(CREATE_HEADLESS_CART_ERROR);

const CREATE_CART = 'cart/create';
const initiateCreateCart = createAction<void, typeof CREATE_CART>(CREATE_CART);

export const attemptCreateHeadlessCart =
  ({
    storeId,
    products,
    reservationMode,
    externalSource,
    headlessCheckoutPartnerId,
    loginURL,
    disableGuestCheckout,
  }: {
    disableGuestCheckout?: boolean;
    externalSource?: string;
    headlessCheckoutPartnerId?: string;
    loginURL?: string;
    products: HeadlessCartProduct[];
    reservationMode: 'pickup' | 'delivery';
    storeId: string;
  }): CustomerThunkAction =>
  async (dispatch, getState, extraArgsRef) => {
    const appMode = extraArgsRef?.current?.appMode;
    const { application } = getState();

    dispatch(initiateCreateCart());

    const body = {
      app_mode: appMode,
      app_mode_name: appMode,
      disable_guest_checkout: disableGuestCheckout,
      external_source: externalSource,
      headless_checkout_partner_id: headlessCheckoutPartnerId,
      login_url: loginURL,
      products,
      reservation_mode: reservationMode,
      store_id: storeId,
      user_agent: navigator.userAgent,
      j_device_id: application?.janeDeviceId,
    };

    CartSource.create(body)
      .then((result: { cart: Cart }) => {
        dispatch(createHeadlessCartSuccess(result));
      })
      .catch(() => {
        dispatch(createHeadlessCartError());
      });
  };

const ADDING_ITEM_TO_CART = 'cart/adding-item-to-cart';
const initiateAddingItemToCart = createAction<void, typeof ADDING_ITEM_TO_CART>(
  ADDING_ITEM_TO_CART
);
const ADDING_ITEM_TO_CART_FAILED = 'cart/adding-item-to-cart-failed';
const addingItemToCartFailed = createAction<
  void,
  typeof ADDING_ITEM_TO_CART_FAILED
>(ADDING_ITEM_TO_CART_FAILED);

export interface AttemptToAddToCartPayload extends Pick<
  AddedOrUpdatedProductInCart,
  'columnPosition' | 'location' | 'productLocation' | 'rowPosition'
> {
  almostGone?: AlmostGone;
  count: number;
  menuProduct: MenuProduct;
  moeTrack: (event: MoeEvent) => void;
  navigate: NavigateFunction;
  price_id: PriceId;
  source: string;
  special?: StoreSpecial;
  store: Store;
  userPreferences: UserPreferencesType;
  userSegments?: UserSegmentIds;
  withoutDrawer?: boolean;
}

export const attemptToAddToCart =
  ({
    almostGone,
    columnPosition,
    count,
    location,
    menuProduct,
    moeTrack,
    navigate,
    price_id,
    productLocation,
    rowPosition,
    source,
    special,
    store,
    userPreferences,
    userSegments,
    withoutDrawer = false,
  }: AttemptToAddToCartPayload): CustomerThunkAction =>
  (dispatch, getState, extraArgsRef) => {
    const appMode = extraArgsRef?.current?.appMode;
    const {
      cart: { cart },
    } = getState();

    const storeAndProduct = prepareCartProduct(
      store,
      menuProduct,
      price_id,
      count
    );

    if (cart.store && cart.store.id !== store.id) {
      return maybeChangeStore(appMode, dispatch, storeAndProduct, navigate);
    }

    const productInCart = isProductInCart(cart, menuProduct.id, price_id);
    if (productInCart) {
      trackCartEvent({
        count,
        event: EventNames.UpdatedProductInCart,
        location,
        menuProduct,
        price_id,
        productLocation,
        source,
        special,
        store,
        userPreferences,
        userSegments,
      });
      dispatch(
        updateCart(
          {
            count,
            price_id,
            product_id: menuProduct.id,
          },
          moeTrack
        )
      );
    } else {
      const basePrice = Number(menuProduct[`price_${price_id}`]);
      const discountedPrice = Number(
        menuProduct[`discounted_price_${price_id}`]
      );
      postAnalyticsEvent({
        name: 'cartItemAdd',
        properties: {
          product: {
            price_id,
            ...pickAnalyticsProductProperties<DeepReadonly<MenuProduct>>(
              menuProduct,
              ['product_id', 'name', 'brand', 'category', 'kind']
            ),
          },
          productId: menuProduct.product_id || menuProduct.id,
          quantity: count,
          ...(basePrice > 0 && { price: basePrice }),
          ...(discountedPrice > 0 && { discountedPrice }),
        },
      });

      trackCartEvent({
        almostGone,
        cartId: cart.id,
        columnPosition,
        count,
        event: EventNames.AddedProductToCart,
        location,
        menuProduct,
        price_id,
        productLocation,
        rowPosition,
        source,
        special,
        store,
        userPreferences,
        userSegments,
      });
      dispatch(initiateAddingItemToCart());
      dispatch(addToCart(storeAndProduct, withoutDrawer, moeTrack));
    }
  };

const maybeChangeStore = (
  appMode: AppMode,
  dispatch: AppDispatch,
  storeAndProduct: StoreAndCartProduct,
  navigate: NavigateFunction
) => {
  dispatch(prepareToReplaceCart(storeAndProduct));

  if (appMode === 'default') {
    return dispatch(openModal({ name: 'replaceCart' }));
  }
  return navigate(paths.embeddedReplaceCart());
};

const isProductInCart = (
  cart: DeepReadonly<Cart>,
  productId: string | number,
  price_id: PriceId
) =>
  cart.products.find(
    (product) => product.id === productId && product.price_id === price_id
  );

const matchError = (error: string): CartActions => {
  switch (error) {
    case 'Cart is not pending':
      return initiateClearCart();
  }

  return addingItemToCartFailed();
};

export interface CartUpdatePayload {
  count: number;
  price_id: PriceId;
  product_id: number;
}

export interface CartAddPayload {
  cartUuid: string;
  product: PendingCartProduct;
  store: Store;
  userAgent: string;
}

interface CartDeleteItemPayload {
  priceId: PriceId;
  productId: string | number;
}

export interface CartUpdateDeliveryAddressPayload {
  deliveryAddress: Address;
}

export type CartActions =
  | ReturnType<typeof openCart>
  | ReturnType<typeof setHeadlessV2Cart>
  | ReturnType<typeof closeCart>
  | ReturnType<typeof initiateAddingItemToCart>
  | ReturnType<typeof addingItemToCartFailed>
  | ReturnType<typeof initiateCreateCart>
  | ReturnType<typeof initiateGetCart>
  | ReturnType<typeof initiateGetRemoteCart>
  | ReturnType<typeof getRemoteCartSucceeded>
  | ReturnType<typeof initiateClearCart>
  | ReturnType<typeof clearCartError>
  | ReturnType<typeof cartDeleteItemSuccess>
  | ReturnType<typeof cartUpdateTags>
  | ReturnType<typeof updateTip>
  | ReturnType<typeof updatePaymentMethod>
  | ReturnType<typeof cartUpdatePromoCode>
  | ReturnType<typeof initiateRemoveSpecialHasChangedFlag>
  | ReturnType<typeof initiateDeleteUnavailableProducts>
  | ReturnType<typeof dismissCheckoutError>
  | ReturnType<typeof cartUpdateReservationMode>
  | ReturnType<typeof initiateAnalyzeSpecials>
  | ReturnType<typeof initiateApplyCrmRedemption>
  | ReturnType<typeof applyCrmRedemptionError>
  | ReturnType<typeof updateCrmRedemptions>
  | ReturnType<typeof removeCrmRedemptionError>
  | ReturnType<typeof removeCrmRedemptionSuccess>
  | ReturnType<typeof fixUnavailableCheckout>
  | ReturnType<typeof unavailableCheckoutFixed>
  | ReturnType<typeof initiateCheckout>
  | ReturnType<typeof cartUpdateSuccess>
  | ReturnType<typeof cartAddSuccess>
  | ReturnType<typeof prepareToReplaceCart>
  | ReturnType<typeof initiateGetLocalCartSuccess>
  | ReturnType<typeof getCartSucceeded>
  | ReturnType<typeof getUserCartSucceeded>
  | ReturnType<typeof refreshCartSuccess>
  | ReturnType<typeof paytenderCheckoutCancelled>
  | ReturnType<typeof checkoutSucceeded>
  | ReturnType<typeof checkoutError>
  | ReturnType<typeof analyzeSpecialsSuccess>
  | ReturnType<typeof getCartError>
  | ReturnType<typeof deleteUnavailableProductsSuccess>
  | ReturnType<typeof clearRemovedCrmRedemptions>
  | ReturnType<typeof removeSpecialHasChangedFlagSuccess>
  | ReturnType<typeof removeBrandSpecialHasChangedFlag>
  | ReturnType<typeof createHeadlessCartSuccess>
  | ReturnType<typeof createHeadlessCartError>
  | ReturnType<typeof initiateUpdateDeliveryAddress>
  | ReturnType<typeof removeDeliveryAddress>
  | ReturnType<typeof setUserGroupSpecialId>;

type StoreAndCartProduct = DeepReadonly<{
  product: PendingCartProduct;
  store: Store;
}>;

export type CartState = DeepReadonly<{
  analyzingSpecials: boolean;
  cart: Cart & { status: string | undefined };
  checkoutError: string | null;
  deliveryAddress: Address | null;
  hasCheckoutError: boolean;
  hasLoaded: boolean;
  isAddingItemToCart: boolean;
  isApplyingCrmRedemption: boolean;
  isChangingReservationModes: boolean;
  isCheckingOut: boolean;
  isDeletingUnavailableProducts: boolean;
  isFixingUnavailableCheckout: boolean;
  isLoading: boolean;
  isRemovingSpecialHasChangedFlag: boolean;
  queryPromoCode: string | undefined | null;
  replacementCart: null | StoreAndCartProduct;
  salesTaxLoading: boolean;
  selectedPaymentMethod: string | null;
  tags: object | undefined;
  userGroupSpecialId?: string | number;
  /** iso8601 datetime */
  wasLoadedAt: string | undefined;
}>;

const getDefaultCart = (): CartState['cart'] => ({
  all_discounts_total: 0.0,
  brand_discounts: [],
  brand_discounts_service_fee_total: 0.0,
  brand_discounts_total: 0.0,
  brand_discounts_total_with_fee: 0.0,
  brand_special_has_changed: false,
  cart_discount_amount: 0.0,
  cart_discount_special_id: undefined,
  crm_redemptions: [],
  delivery_fee_amount: 0.0,
  discounted_product_total: 0.0,
  discounted_subtotal: 0.0,
  group_discounts_total: 0.0,
  group_special_id: null,
  group_special_title: null,
  headless_v2: false,
  is_open: false,
  item_total: 0.0,
  jane_pay_service_fee_amount: 0.0,
  loyalty_points_stacking_enabled: true,
  max_specials_per_cart: null,
  max_specials_per_cart_hit: false,
  order_total: 0.0,
  product_discounts_total: 0.0,
  products: [],
  removed_crm_redemptions: [],
  reservation_mode: 'pickup',
  rounding_amount: 0.0,
  sales_tax_amount: 0.0,
  sales_tax_rate: 0.0,
  service_fee_amount: 0.0,
  special_has_changed: false,
  status: undefined,
  store: undefined,
  store_tax_amount: 0.0,
  tax_included: false,
  tip_amount: '0.0',
  unavailable_products: [],
  uuid: '',
});

const getInitialState = (): CartState => ({
  analyzingSpecials: false,
  cart: getDefaultCart(),
  checkoutError: null,
  deliveryAddress: Storage.get('deliveryAddress') || null,
  hasCheckoutError: false,
  hasLoaded: false,
  isAddingItemToCart: false,
  isApplyingCrmRedemption: false,
  isChangingReservationModes: false,
  isCheckingOut: false,
  isDeletingUnavailableProducts: false,
  isFixingUnavailableCheckout: false,
  isLoading: false,
  isRemovingSpecialHasChangedFlag: false,
  queryPromoCode: undefined,
  replacementCart: null,
  salesTaxLoading: false,
  selectedPaymentMethod: null,
  tags: undefined,
  userGroupSpecialId: undefined,
  wasLoadedAt: undefined,
});

const clear = (state: CartState) => ({
  ...state,
  cart: getDefaultCart(),
  deliveryAddress: null,
  replacementCart: null,
  userGroupSpecialId: undefined,
});

export const cartReducer: Reducer<CartState, CustomerAction> = (
  state = getInitialState(),
  action
) => {
  const { cart } = state;

  switch (action.type) {
    case OPEN_CART: {
      return { ...state, cart: { ...cart, is_open: true } };
    }

    case SET_HEADLESS_V2_CART: {
      return { ...state, cart: { ...cart, headless_v2: true } };
    }

    case CLOSE_CART: {
      return { ...state, cart: { ...cart, is_open: false } };
    }

    case ADDING_ITEM_TO_CART: {
      return { ...state, isAddingItemToCart: true };
    }

    case ADDING_ITEM_TO_CART_FAILED: {
      return { ...state, isAddingItemToCart: false };
    }

    case CART_ADD: {
      const { store, product, userAgent, cartUuid } = action.payload;
      return {
        ...state,
        cart: {
          ...cart,
          products: cart.products.concat(product),
          status: 'pending',
          store,
          user_agent: userAgent,
          uuid: cartUuid,
        },
        isAddingItemToCart: false,
      };
    }

    case GET_CART_ERROR:
    case CART_CLEAR_ERROR:
    case CART_CLEAR:
      return clear({
        ...state,
        hasLoaded: true,
        isAddingItemToCart: false,
        isLoading: false,
      });

    case CART_UPDATE: {
      const { cart } = state;
      const updatedProduct = action.payload;

      const products = cart.products.map((product) =>
        product.id === updatedProduct.product_id &&
        product.price_id === updatedProduct.price_id
          ? {
              ...product,
              count: updatedProduct.count,
            }
          : product
      );
      return {
        ...state,
        cart: {
          ...cart,
          products,
        },
      };
    }

    case PAYTENDER_CHECKOUT_CANCELLED:
      return { ...state, isCheckingOut: false };

    case CHECKOUT:
      return { ...state, isCheckingOut: true };

    case CHECKOUT_SUCCESS:
      return { ...clear(state), hasLoaded: true, isCheckingOut: false };

    case CHECKOUT_ERROR:
      return {
        ...state,
        checkoutError:
          typeof action.payload === 'string'
            ? action.payload
            : 'Something went wrong, please try again. If the problem persists, please contact us at support@iheartjane.com',
        hasCheckoutError: true,
        isCheckingOut: false,
      };

    case DISMISS_CHECKOUT_ERROR:
      return { ...state, checkoutError: null, hasCheckoutError: false };

    case CREATE_CART:
      return { ...state, isLoading: true };

    case GET_CART:
      return { ...state, isLoading: true };

    case GET_REMOTE_CART_SUCCESS:
      return {
        ...state,
        wasLoadedAt: new Date().toISOString(),
      };

    case GET_USER_CART_SUCCESS: {
      const { cart } = state;
      /**
       * the cart/get_for_user endpoint doesn't know the user address
       * since it can be called from any point of the app after log in
       * so we won't overwrite any work done on the cart that may have
       * updated its address/delivery_fee since that's the last true value
       * we have to do the same for the order total, since the one from the
       * get_for_user endpoint won't add the delivery fee either
       */
      return {
        ...state,
        cart: {
          ...getDefaultCart(),
          ...action.payload.cart,
          delivery_fee_amount: cart.delivery_fee_amount || 0,
          headless_v2: state.cart.headless_v2,
          is_open: state.cart.is_open,
          order_total: cart.order_total,
        },
        hasLoaded: true,
        isChangingReservationModes: false,
        isLoading: false,
      };
    }

    case GET_CART_SUCCESS:
      return {
        ...state,
        cart: {
          ...getDefaultCart(),
          ...action.payload.cart,
          headless_v2: state.cart.headless_v2,
          is_open: state.cart.is_open,
        },
        hasLoaded: true,
        isChangingReservationModes: false,
        isLoading: false,
      };

    case REFRESH_CART:
      return {
        ...state,
        cart: {
          ...getDefaultCart(),
          ...action.payload.cart,
          headless_v2: state.cart.headless_v2,
          is_open: state.cart.is_open,
        },
        hasLoaded: true,
        isLoading: false,
      };

    case CREATE_HEADLESS_CART_SUCCESS:
      return {
        ...state,
        cart: {
          ...getDefaultCart(),
          ...action.payload.cart,
        },
        hasLoaded: true,
        isLoading: false,
      };

    case ANALYZE_SPECIALS:
      return { ...state, analyzingSpecials: true };

    case ANALYZE_SPECIALS_SUCCESS: {
      const { cart, userGroupSpecialId } = action.payload;

      // use only amounts, not the whole cart object, until the cart optimizer stops interpreting product price_id changes as special_has_changed
      const {
        all_discounts_total,
        brand_discounts,
        brand_discounts_service_fee_total,
        brand_discounts_total,
        brand_discounts_total_with_fee,
        brand_special_has_changed,
        cart_discount_amount,
        cart_discount_special_id,
        crm_redemptions,
        delivery_fee_amount,
        discounted_product_total,
        discounted_subtotal,
        group_discounts_total,
        group_special_id,
        group_special_title,
        item_total,
        jane_pay_service_fee_amount,
        order_total,
        product_discounts_total,
        products,
        promo_code,
        removed_crm_redemptions,
        rounding_amount,
        sales_tax_amount,
        sales_tax_rate,
        service_fee_amount,
        store_tax_amount,
        unavailable_products,
      } = cart;

      return {
        ...state,
        analyzingSpecials: false,
        cart: {
          ...state.cart,
          all_discounts_total,
          brand_discounts,
          brand_discounts_service_fee_total,
          brand_discounts_total,
          brand_discounts_total_with_fee,
          brand_special_has_changed,
          cart_discount_amount,
          cart_discount_special_id,
          crm_redemptions,
          delivery_fee_amount,
          discounted_product_total,
          discounted_subtotal,
          group_discounts_total,
          group_special_id,
          group_special_title,
          item_total,
          jane_pay_service_fee_amount,
          order_total,
          product_discounts_total,
          products,
          promo_code,
          removed_crm_redemptions,
          rounding_amount,
          sales_tax_amount,
          sales_tax_rate,
          service_fee_amount,
          store_tax_amount,
          unavailable_products,
        },
        userGroupSpecialId,
      };
    }

    case APPLY_CRM_REDEMPTION: {
      return {
        ...state,
        isApplyingCrmRedemption: true,
      };
    }

    case UPDATE_CRM_REDEMPTIONS: {
      return {
        ...state,
        cart: {
          ...state.cart,
          ...action.payload.cart,
        },
        isApplyingCrmRedemption: false,
      };
    }

    case REMOVE_CRM_REDEMPTION_SUCCESS: {
      const { id } = action.payload;
      return {
        ...state,
        cart: {
          ...state.cart,
          crm_redemptions: state.cart.crm_redemptions?.filter(
            (redemption) => redemption.id !== id
          ),
        },
      };
    }

    case CLEAR_REMOVED_CRM_REDEMPTIONS: {
      return {
        ...state,
        cart: {
          ...state.cart,
          removed_crm_redemptions: [],
        },
      };
    }

    case CART_UPDATE_TIP: {
      return {
        ...state,
        cart: {
          ...state.cart,
          tip_amount: action.payload,
        },
      };
    }

    case CART_UPDATE_PAYMENT_METHOD: {
      return {
        ...state,
        selectedPaymentMethod: action.payload,
      };
    }

    case CART_DELETE_ITEM:
      if (
        state.cart.products.length === 1 &&
        state.cart.products[0].id === action.payload.productId
      ) {
        return clear(state);
      }

      return {
        ...state,
        cart: {
          ...state.cart,
          products: state.cart.products.filter(
            (product) =>
              !(
                product.id === action.payload.productId &&
                product.price_id === action.payload.priceId
              )
          ),
        },
      };

    case FIX_UNAVAILABLE_CHECKOUT:
      return { ...state, isFixingUnavailableCheckout: true };

    case UNAVAILABLE_CHECKOUT_FIXED:
      return { ...state, isFixingUnavailableCheckout: false };

    case DELETE_UNAVAILABLE_PRODUCTS:
      return { ...state, isDeletingUnavailableProducts: true };

    case DELETE_UNAVAILABLE_PRODUCTS_SUCCESS: {
      const newState = {
        ...state,
        cart: { ...state.cart, unavailable_products: [] },
        checkoutError: null,
        hasCheckoutError: false,
        isDeletingUnavailableProducts: false,
        isFixingUnavailableCheckout: false,
      };

      return state.cart.products.length === 0 ? clear(newState) : newState;
    }

    case REMOVE_SPECIAL_HAS_CHANGED_FLAG:
      return { ...state, isRemovingSpecialHasChangedFlag: true };

    case REMOVE_SPECIAL_HAS_CHANGED_FLAG_SUCCESS:
      return {
        ...state,
        cart: { ...cart, special_has_changed: false },
        isRemovingSpecialHasChangedFlag: false,
      };

    case REMOVE_BRAND_SPECIAL_HAS_CHANGED_FLAG_SUCCESS:
      return {
        ...state,
        cart: {
          ...cart,
          brand_special_has_changed: false,
        },
      };

    case PREPARE_TO_REPLACE_CART:
      return { ...state, replacementCart: action.payload };

    case CART_UPDATE_RESERVATION_MODE:
      return {
        ...state,
        isChangingReservationModes: true,
        cart: {
          ...state.cart,
          reservation_mode: action.payload.reservationMode,
        },
      };

    case CART_UPDATE_TAGS:
      return {
        ...state,
        tags: action.payload,
      };

    case CART_UPDATE_PROMO_CODE:
      return {
        ...state,
        queryPromoCode: action.payload,
      };

    case SET_USER_GROUP_SPECIAL_ID: {
      const { id: userGroupSpecialId } = action.payload;

      return {
        ...state,
        analyzingSpecials: false,
        userGroupSpecialId,
      };
    }

    case CART_UPDATE_DELIVERY_ADDRESS:
      return {
        ...state,
        deliveryAddress: action.payload.deliveryAddress,
      };
    case CART_REMOVE_DELIVERY_ADDRESS:
      return {
        ...state,
        deliveryAddress: null,
      };
  }

  return state;
};

export const cartStorageKey = (
  appMode: AppMode,
  storeId: string | number | undefined
) => {
  if (['embedded', 'whiteLabel', 'framelessEmbed'].includes(appMode)) {
    return `cart-${appMode}-${storeId}`;
  }

  return `cart-${appMode}`;
};

export const persistCartToLocalStorage = (
  store: ReturnType<typeof configureCustomerStore>,
  appMode: AppMode
) => {
  let lastAppMode: AppMode | undefined = undefined;
  let lastCart: CartState['cart'] | null = null;
  let lastDeliveryAddress: CartState['deliveryAddress'] | null = null;
  let lastTags: CartState['tags'] | undefined = undefined;
  let lastQueryPromoCode: CartState['queryPromoCode'] | undefined = undefined;

  store.subscribe(() => {
    const {
      cart: { cart, deliveryAddress, queryPromoCode, tags },
      embeddedApp: { partnerId },
    } = store.getState();

    const cartKey = cartStorageKey(appMode, partnerId);

    const persist = (cart: CartState['cart']) => {
      if (lastCart === cart) return;

      lastCart = cart;

      const localCart = {
        products: cart.products.map((product) => ({
          count: product.count,
          id: product.id,
          price_id: product.price_id,
        })),
        reservation_mode: cart.reservation_mode,
        status: cart.status,
        store: cart.store && pick(cart.store, ['id', 'name']),
        uuid: cart.uuid,
      };

      Storage.set(cartKey, localCart);
    };

    if (lastTags !== tags) {
      lastTags = tags;
      Storage.set(`${cartKey}-tags`, tags);
    }

    if (lastQueryPromoCode !== queryPromoCode) {
      lastQueryPromoCode = queryPromoCode;
      Storage.set(`${cartKey}-promo-code`, queryPromoCode);
    }

    if (appMode !== lastAppMode) {
      const cartKey = cartStorageKey(appMode, partnerId);
      lastAppMode = appMode;
      Storage.set(`${cartKey}-promo-code`, queryPromoCode);
      Storage.set(`${cartKey}-tags`, tags);
    }

    if (lastDeliveryAddress !== deliveryAddress) {
      lastDeliveryAddress = deliveryAddress;
      Storage.set('deliveryAddress', deliveryAddress);
    }

    if (cart.status || lastCart) {
      persist(cart);
    }
  });
};

export const selectCart = (state: AppState) => state.cart;
